import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    InvoiceAdditionalFilters,
    InvoiceFeedInput,
    InvoiceFeedResponse,
    InvoiceStatus,
    InvoiceType,
    InvoiceEntry,
} from '../../types/invoice';

// Re-export commonly used invoice feed types so existing imports continue to work.
export type {
    InvoiceStatus,
    InvoiceType,
    InvoiceEntry,
    InvoiceAdditionalFilters,
};

const getInvoicesFeed = async (
    sessionId: string,
    input: InvoiceFeedInput
): Promise<InvoiceFeedResponse> => {
    const { data } = await axios.post(`${API_URL}/invoices/feed`,
        input,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export function invoicesFeedQueryKey(args: {
    organizationId: string;
    type: 'all' | 'flagged' | 'submitted';
    archivedFilter: 'active' | 'archived' | 'all';
    locationIds: Array<string> | 'all';
    page: number;
    limit: number;
    search: string;
    additionalFilters: InvoiceAdditionalFilters;
}) {
    const effectiveLocationIds = normalizeInvoicesFeedLocationIds(args.locationIds);
    return [
        '/invoices/feed',
        args.organizationId,
        args.type,
        args.archivedFilter,
        effectiveLocationIds === 'all' ? 'all' : effectiveLocationIds.join(','),
        args.page,
        args.limit,
        args.search,
        JSON.stringify(args.additionalFilters),
    ] as const;
}

/** One-shot feed page. Not tied to the list query, so a later archive refetch cannot replace it. */
export function fetchInvoicesFeedPage(
    sessionId: string,
    args: {
        type: 'all' | 'flagged' | 'submitted';
        archivedFilter: 'active' | 'archived' | 'all';
        locationIds: Array<string> | 'all';
        page: number;
        limit: number;
        search: string;
        additionalFilters: InvoiceAdditionalFilters;
    },
): Promise<InvoiceFeedResponse> {
    const effectiveLocationIds = normalizeInvoicesFeedLocationIds(args.locationIds);
    return getInvoicesFeed(sessionId, {
        type: args.type,
        archivedFilter: args.archivedFilter,
        locationIds: effectiveLocationIds,
        limit: args.limit,
        offset: (args.page - 1) * args.limit,
        search: args.search,
        additionalFilters: Object.keys(args.additionalFilters).length > 0 ? args.additionalFilters : undefined,
    });
}

/** Empty selection means "all" (matches invoice report and API session scoping). */
export function normalizeInvoicesFeedLocationIds(
    locationIds: Array<string> | 'all',
): Array<string> | 'all' {
    if (locationIds === 'all') return 'all';
    if (!Array.isArray(locationIds) || locationIds.length === 0) return 'all';
    return locationIds;
}

export const useInvoicesFeed = (
    type: 'all' | 'flagged' | 'submitted' = 'all',
    archivedFilter: 'active' | 'archived' | 'all' = 'active',
    locationIds: Array<string> | 'all',
    page: number = 1,
    limit: number = 20,
    search: string = '',
    additionalFilters: InvoiceAdditionalFilters = {},
    enabled: boolean = true,
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: invoicesFeedQueryKey({
            organizationId: sessionContext?.organizationId ?? '',
            type,
            archivedFilter,
            locationIds,
            page,
            limit,
            search,
            additionalFilters,
        }),
        queryFn: () =>
            fetchInvoicesFeedPage(sessionContext?.sessionId ?? '', {
                type,
                archivedFilter,
                locationIds,
                page,
                limit,
                search,
                additionalFilters,
            }),
        enabled: enabled && !!sessionContext?.sessionId,
        // Override app-wide staleTime so returning to /invoices always loads a fresh table.
        staleTime: 0,
        refetchOnMount: 'always',
        refetchInterval: 1000 * 60 * 5,
    });
};

export const useInvoiceVendorOptions = (
    type: 'all' | 'flagged' | 'submitted',
    archivedFilter: 'active' | 'archived' | 'all',
    locationIds: Array<string> | 'all',
    enabled: boolean,
) => {
    const sessionContext = useContext(SessionContext);
    const effectiveLocationIds = normalizeInvoicesFeedLocationIds(locationIds);

    return useQuery({
        queryKey: [
            '/invoices/feed',
            'vendorOptions',
            sessionContext?.organizationId ?? '',
            type,
            archivedFilter,
            effectiveLocationIds === 'all' ? 'all' : effectiveLocationIds.join(','),
        ],
        queryFn: () =>
            getInvoicesFeed(sessionContext?.sessionId ?? '', {
                type,
                archivedFilter,
                locationIds: effectiveLocationIds,
                limit: 0,
                offset: 0,
                vendorOptionsOnly: true,
            }),
        enabled: !!sessionContext?.sessionId && enabled,
        staleTime: 30_000,
        select: (data) => data.vendorOptions ?? [],
    });
};

